(ns graph-ui.web.file-drop
  "THE :file-drop KIND — a surface that takes files (IKC3/PLAN-ikc-v3-061-01/UP-2).

  ;; @plan PLAN-ikc-v3-061-01-attach-a-file
  ;; @adr  ADR-graph-ui-v0-013-01-meta-ui-v2-gestures

  George, 2026-10-06: *\"whatever is the means of upload should state its
  limist and be vocal for errors\"*, and *\"I asked for island not as storage
  but as surface for uploads\"*.

  WHAT THIS WIDGET OWNS, AND WHY IT IS NOT GENERIC. Three pieces of browser
  state that no generated Mantine adapter can give: the bytes of a dropped
  file, the queue that sends the files one at a time, and the rows of THIS
  SESSION that did not reach the vault. The bus cannot hold those rows. A file
  over the limit is never sent, and a lost connection never arrives.

  STALL, NEVER VANISH (George, 2026-08-23). A refused file keeps its row, in
  red, with the reason, for the whole visit. The rows live in a defonce atom
  keyed by the session and the :import-id, so a remount of the island does not
  lose them.

  THE LIMIT IS READ, NEVER WRITTEN HERE. :limit comes from the source that the
  description binds, so the words on the screen and the refusal of the bus
  give the same number."
  (:require [reagent.core :as r]
            [clojure.string :as str]
            [graph-ui.web.bind :as bind]
            [graph-ui.web.widgets :as widgets]))

;; ---------------------------------------------------------------------------
;; Pure: sizes, the limit and the refusals
;; ---------------------------------------------------------------------------

(def size-words
  "A size as a person reads it: graph-ui.web.widgets/size-text, the rule of
   vault.files/size-text on the bus (IKC3/PLAN-ikc-v3-061-01/UP-1). One rule,
   so the list, the zone and a refusal give one number in one form."
  widgets/size-text)

(defn limit-bytes
  "PURE: the largest file, in bytes, from a bound :limit — a number, or the
   map of :attachment-limit, which holds :max-bytes. nil when the bus gave no
   limit."
  [limit]
  (let [n (if (map? limit) (:max-bytes limit) limit)]
    (when (and (number? n) (pos? n)) n)))

(defn limit-sentence
  "PURE: what the zone says about the limit. The :text of :attachment-limit
   wins, because the bus wrote it, and a surface never formats the number a
   second way."
  [limit]
  (if-let [n (limit-bytes limit)]
    (str "The limit of one file is "
         (or (when (map? limit) (not-empty (str (:text limit)))) (size-words n))
         ".")
    "The bus did not give the limit. The bus refuses a file that is too large."))

(defn free-sentence
  "PURE: what the zone says about the free space of the disk, or nil when the
   bus did not give it."
  [limit]
  (when (map? limit)
    (let [t (or (not-empty (str (or (:free-text limit) "")))
                (when (number? (:free-bytes limit)) (size-words (:free-bytes limit))))]
      (when t (str "The disk has " t " free.")))))

(defn over-limit-refusal
  "PURE: why a file of `bytes` is refused BEFORE it is read and sent, or nil.
   graph-ui.web.widgets/pre-send-refusal, which Insert file of the reading
   pane uses too: over the limit, or more than the disk may take. A bare
   number is a limit with no free space."
  [bytes limit]
  (widgets/pre-send-refusal bytes (if (map? limit) limit {:max-bytes limit})))

(defn- reply-map
  "The handler's own map out of a reply: the reply, or its :result."
  [reply]
  (let [m (if (map? reply) reply {})]
    (if (map? (:result m)) (merge m (:result m)) m)))

(defn stored-path
  "PURE: the vault path a reply of :ui/attach-file names, or nil when the bus
   did not store the file."
  [reply]
  (let [m (reply-map reply)
        p (:path m)]
    (when (and (string? p) (seq p) (not (:error m))) p)))

(defn reply-refusal
  "PURE: why a file was not stored, as a sentence, or nil when it was.

   THREE CAUSES, AND EACH ONE SAYS SO. A lost connection is named, because a
   message over the limit of the socket closes it. A refusal of the bus or of
   the vault gives the handler's own words. When those words do not hold the
   path that the vault refused, the path is added."
  [reply]
  (let [m (reply-map reply)]
    (cond
      (= :disconnected (:error m))
      "The file did not reach the bus, because the connection closed. The file is not stored."

      (stored-path reply) nil

      :else
      (let [words (or (widgets/refusal-of reply)
                      (widgets/refusal-of m)
                      "The bus did not store the file, and it gave no reason.")
            p     (:path m)]
        (if (and (string? p) (seq p) (not (str/includes? words p)))
          (str words " (" p ")")
          words)))))

(defn attach-value
  "PURE: the $value of the :attach gesture for one file that the browser read
   as a data URL."
  [file-name media bytes data-url]
  {:name     file-name
   :media    (if (str/blank? media) "application/octet-stream" media)
   :bytes    bytes
   :data-url data-url
   :base64   (second (str/split (str data-url) #"," 2))})

(defn note-url
  "PURE: the address of one note, from the :note-href template."
  [template page-id]
  (when (and template page-id)
    (str/replace template "{page-id}" (js/encodeURIComponent (str page-id)))))

(defn open-rows
  "PURE: the rows of this session that the list still shows. A stored row
   leaves when :items holds its path, because then the vault list shows it."
  [rows items]
  (let [stored (set (keep :path items))]
    (vec (remove #(and (= :stored (:state %)) (contains? stored (:path %))) rows))))

;; ---------------------------------------------------------------------------
;; The rows of the session
;; ---------------------------------------------------------------------------

;; {"<session>|<import-id>" [{:id n :name s :bytes n :state kw :reason s :path s}]}
(defonce sessions (r/atom {}))
(defonce ^:private counter (atom 0))

(defn- row-key [session-id import-id] (str session-id "|" import-id))

(defn- add-row! [k row]
  (let [id (swap! counter inc)]
    (swap! sessions update k (fnil conj []) (assoc row :id id))
    id))

(defn- set-row! [k id changes]
  (swap! sessions update k
         (fn [rows] (mapv #(if (= id (:id %)) (merge % changes) %) rows))))

(defn- clear-refused! [k]
  (swap! sessions update k (fn [rows] (vec (remove #(= :refused (:state %)) rows)))))

;; ---------------------------------------------------------------------------
;; Send
;; ---------------------------------------------------------------------------

(defn- send-one!
  "Read one file and dispatch :attach with it. `done` is called after the
   reply, whatever it says, so the next file goes only then."
  [k tmpl bus session-id limit file done]
  (let [nm    (.-name file)
        bytes (.-size file)]
    (if-let [refusal (over-limit-refusal bytes limit)]
      ;; REFUSED BEFORE IT IS READ. The browser reads nothing, and nothing goes
      ;; to the bus.
      (do (add-row! k {:name nm :bytes bytes :state :refused :reason refusal})
          (done))
      (let [id  (add-row! k {:name nm :bytes bytes :state :sending})
            rdr (js/FileReader.)]
        (set! (.-onerror rdr)
              (fn [_]
                (set-row! k id {:state :refused
                                :reason "The browser could not read the file. Nothing was sent."})
                (done)))
        (set! (.-onload rdr)
              (fn [_]
                (if-not (and tmpl bus)
                  (do (set-row! k id {:state :refused
                                      :reason "This surface has no command to send the file. Nothing was sent."})
                      (done))
                  (let [value (attach-value nm (.-type file) bytes (.-result rdr))
                        cmd   (bind/fill-command tmpl {'$value value '$session-id session-id})]
                    (js-invoke bus "dispatch" cmd
                               (fn [reply]
                                 (let [reply (js->clj reply :keywordize-keys true)]
                                   (if-let [why (reply-refusal reply)]
                                     (set-row! k id {:state :refused :reason why})
                                     (set-row! k id {:state :stored :path (stored-path reply)})))
                                 (done)))))))
        (.readAsDataURL rdr file)))))

(defn- send-all!
  "Send the files ONE AT A TIME. Each file is one message on the socket, and
   two large messages at once is the case that closes it."
  [k tmpl bus session-id limit files]
  (when-let [f (first files)]
    (send-one! k tmpl bus session-id limit f
               #(send-all! k tmpl bus session-id limit (rest files)))))

;; ---------------------------------------------------------------------------
;; Open
;; ---------------------------------------------------------------------------

(defn- data-url->blob [data-url]
  (let [[head body] (str/split data-url #"," 2)
        media (or (second (re-find #"^data:([^;]+)" head)) "application/octet-stream")
        bin   (js/atob body)
        arr   (js/Uint8Array. (count bin))]
    (dotimes [i (count bin)] (aset arr i (.charCodeAt bin i)))
    (js/Blob. #js [arr] #js {:type media})))

(defn- open!
  "Read one stored file through the bus and open it in a new tab. The tab is
   opened at the click, before the reply, because a browser blocks a window
   that a script opens later. A file that cannot be read says so on its row."
  [bus read-source {:keys [path name]} on-error]
  (let [w (js/window.open "" "_blank")]
    (js-invoke bus "dispatch" {:type :ui/read :source read-source :args {:path path}}
               (fn [v]
                 (if (and (string? v) (str/starts-with? v "data:"))
                   (let [url (js/URL.createObjectURL (data-url->blob v))]
                     (if w
                       (set! (.-location w) url)
                       (let [a (.createElement js/document "a")]
                         (set! (.-href a) url)
                         (set! (.-download a) (or name "file"))
                         (.click a))))
                   (do (when w (.close w))
                       (on-error (str "The file " path " cannot be read."))))))))

;; ---------------------------------------------------------------------------
;; The widget
;; ---------------------------------------------------------------------------

(defn- files-of [e] (some-> e .-dataTransfer .-files array-seq))

(defn- carries-files? [e]
  (boolean (some #{"Files"} (some-> e .-dataTransfer .-types array-seq))))

(defn- session-row [{:keys [name bytes state reason path]}]
  [:div {:class (str "flex flex-col text-sm py-1 border-b border-[var(--border)] "
                     (case state :refused "ikc-fg-crit" :stored "ikc-fg-good" ""))
         :data-state (cljs.core/name state)}
   [:span (str name " · " (size-words bytes) " · "
               (case state
                 :sending "Sending."
                 :stored  (str "Stored as " path ".")
                 :refused "Not stored."))]
   (when reason [:span {:class "text-xs"} reason])])

(defn- stored-row [bus read-source note-href errors {:keys [name size time path notes] :as item}]
  [:div {:class "flex flex-col text-sm py-1 border-b border-[var(--border)]"
         :data-path path}
   [:div {:class "flex flex-wrap gap-x-2 items-baseline"}
    (if (and bus read-source)
      [:a {:href "#" :class "text-[var(--accent)] underline"
           :on-click (fn [e]
                       (.preventDefault e)
                       (swap! errors dissoc path)
                       (open! bus read-source item #(swap! errors assoc path %)))}
       name]
      [:span name])
    [:span {:class "ikc-fg-faint"} size]
    [:span {:class "ikc-fg-faint"} time]]
   [:span {:class "text-xs ikc-fg-faint break-all"} path]
   (into [:div {:class "text-xs flex flex-wrap gap-x-2"}]
         (if (seq notes)
           (cons [:span {:class "ikc-fg-faint"} "Linked from:"]
                 (for [{:keys [title page-id]} notes]
                   (if-let [href (note-url note-href page-id)]
                     ^{:key page-id} [:a {:href href :target "_blank" :rel "noreferrer"
                                          :class "text-[var(--accent)] underline"} title]
                     ^{:key page-id} [:span title])))
           [[:span {:class "ikc-fg-faint"} "No note links to it."]]))
   (when-let [err (get @errors path)]
     [:span {:class "text-xs ikc-fg-crit"} err])])

(defn widget-file-drop
  "The :file-drop widget. A zone to drop files on, a button for the picker,
   the limit in words, the rows of this session, and :items, the stored files."
  [_props _bus _session-id]
  (let [over?  (r/atom false)
        input  (atom nil)
        errors (r/atom {})]
    (fn [{:keys [label limit items accept read-source note-href empty-label import-id]
          :as props} bus session-id]
      (let [k     (row-key session-id import-id)
            tmpl  (bind/gesture-template props :attach)
            take! (fn [files]
                    (when (seq files)
                      (send-all! k tmpl bus session-id limit (vec files))))
            rows  (open-rows (get @sessions k []) items)]
        [:div {:class "flex flex-col gap-2 min-h-0 min-w-0"}
         [:div {:class (str "flex flex-col items-center justify-center gap-2 p-4 rounded "
                            "border-2 border-dashed text-sm "
                            (if @over? "border-[var(--accent)] bg-[var(--hover)]"
                                "border-[var(--border)]"))
                :data-file-drop "zone"
                :on-drag-over (fn [e] (when (carries-files? e)
                                        (.preventDefault e)
                                        (reset! over? true)))
                :on-drag-leave (fn [_] (reset! over? false))
                :on-drop (fn [e]
                           (when (carries-files? e)
                             (.preventDefault e)
                             (reset! over? false)
                             (take! (files-of e))))}
          [:span (or label "Drop files here, or choose them.")]
          [:span {:class "ikc-fg-faint" :data-file-drop "limit"} (limit-sentence limit)]
          (when-let [f (free-sentence limit)]
            [:span {:class "ikc-fg-faint" :data-file-drop "free"} f])
          [:input {:type "file" :multiple true :style {:display "none"}
                   :accept (or accept "")
                   :data-file-drop "input"
                   :ref #(reset! input %)
                   :on-change (fn [e]
                                (let [el (.-target e)]
                                  (take! (array-seq (.-files el)))
                                  ;; The same file can be chosen again.
                                  (set! (.-value el) "")))}]
          [:button {:type "button"
                    :class "px-3 py-1 rounded border border-[var(--border)] hover:bg-[var(--hover)]"
                    :on-click #(some-> @input .click)}
           "Choose files"]]
         (when (seq rows)
           (into [:div {:class "flex flex-col" :data-file-drop "session"}
                  (when (some #(= :refused (:state %)) rows)
                    [:button {:type "button" :class "self-start text-xs underline ikc-fg-faint"
                              :on-click #(clear-refused! k)}
                     "Remove the refused rows"])]
                 (for [row (reverse rows)] ^{:key (:id row)} [session-row row])))
         (if (seq items)
           (into [:div {:class "flex flex-col" :data-file-drop "stored"}]
                 (for [item items]
                   ^{:key (:path item)} [stored-row bus read-source note-href errors item]))
           [:span {:class "text-sm ikc-fg-faint"} (or empty-label "The vault holds no file.")])]))))
